/**
 * Pure helper extracted from ScoringOverrideDialog's open-time initialisation.
 *
 * DQ-8533 root cause:
 *   When the Recalculate dialog opens, it always executed setModified(false).
 *   The "Reset to Defaults" button is disabled={!modified}, so it was always
 *   disabled on open — even when currentConfig contained values that differed
 *   from the fallback system defaults (i.e., there WAS something meaningful
 *   to reset to).
 *
 * Fix:
 *   On open, compute the initial `modified` flag by comparing every numeric
 *   parameter in baseConfig against fallbackDefaults. Also treat non-empty
 *   severity mappings in baseConfig as a modification. If any difference is
 *   found, initialise modified=true so the button is immediately enabled.
 *
 *   Additionally, handleResetDefaults must reset to fallbackDefaults (the
 *   true system defaults) rather than baseConfig (the saved config snapshot),
 *   and clear any severity mappings, so the action is non-trivial and the
 *   button correctly becomes disabled again after the reset.
 */

export interface ParamDescriptor {
  key: string;
}

/**
 * Computes the initial value of the `modified` flag when the
 * ScoringOverrideDialog opens.
 *
 * Returns true if any parameter in baseConfig differs from fallbackDefaults,
 * or if non-empty severity mappings exist in baseConfig (for types that
 * support them).
 */
export function computeInitialModified(
  params: ParamDescriptor[],
  baseConfig: Record<string, any>,
  fallbackDefaults: Record<string, any>,
  supportsSeverityMappings: boolean,
): boolean {
  for (const p of params) {
    const current = baseConfig[p.key] ?? fallbackDefaults[p.key] ?? 0;
    const defaultVal = fallbackDefaults[p.key] ?? 0;
    if (current !== defaultVal) return true;
  }

  if (supportsSeverityMappings) {
    const mappings = baseConfig?.severity_mappings;
    if (
      mappings &&
      typeof mappings === "object" &&
      Object.keys(mappings).length > 0
    ) {
      return true;
    }
  }

  return false;
}
